import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  contentClassName?: string;
  /**
   * Width comes from this scale only. Do not override max-width via
   * contentClassName; if no step fits, add one here so every modal stays on
   * the shared scale.
   *
   * sm 448px: confirmations and single-action prompts.
   * md 576px: short forms and pickers.
   * lg 768px: content modals - lists, feeds, long forms.
   * xl 896px: composers and two-pane content.
   * 2xl 1152px: full workspace dialogs (swap, adjust, cycle review).
   * full 95vw: immersive takeovers.
   */
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  /**
   * Close on outside click. Default true. Set false for destructive-guard
   * surfaces (e.g. escalation sheet).
   */
  dismissible?: boolean;
  /** Places a dialog above immersive surfaces such as the full route map. */
  layer?: "default" | "top";
  /**
   * How the dialog arrives. "scale" is the long-standing 600ms grow from 80%.
   * "soft" is the 220ms settle from 95% on the modal easing, for dialogs whose
   * content loads after they open, so the frame does not outshout the content.
   */
  entrance?: "scale" | "soft";
}

const SIZES = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-4xl",
  "2xl": "max-w-6xl",
  full: "max-w-[95vw]",
} as const;

export function Modal({
  open,
  onOpenChange,
  children,
  title,
  description,
  contentClassName,
  size = "md",
  dismissible = true,
  layer = "default",
  entrance = "scale",
}: ModalProps) {
  const layerClass = layer === "top" ? "z-[3000]" : "z-50";
  const entranceClass = entrance === "soft" ? "animate-modal-scale-in" : "animate-scale-in";
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "modal-backdrop fixed inset-0 bg-navy-deep/40 backdrop-blur-sm animate-modal-fade-in",
            layerClass,
          )}
        >
          {/*
           * Backdrop drift: a single very faint teal halo that drifts
           * slowly (48s cycle) inside the dim. Amplitude is intentionally
           * tiny (~3% translate of a 50vw blob) and the colour is
           * teal-600 at 6% alpha through 80px blur, so it's almost
           * imperceptible per-second but stops the backdrop from
           * reading as dead-flat over the 30+ seconds a user spends
           * in a modal. Says "the system is processing this with you"
           * without ever pulling the eye away from modal content.
           */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{
              top: "20%",
              left: "30%",
              width: "50vw",
              height: "50vw",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, color-mix(in srgb, var(--color-teal-600) 4%, transparent), transparent 60%)",
              filter: "blur(80px)",
              animation: "modal-backdrop-drift 48s ease-in-out infinite alternate",
              willChange: "transform",
            }}
          />
        </Dialog.Overlay>
        <Dialog.Content
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
          // Radix warns when Content has no description; opt out explicitly for
          // modals that don't pass one (when present, let Radix auto-associate).
          {...(description ? {} : { "aria-describedby": undefined })}
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-modal focus:outline-none",
            entranceClass,
            layerClass,
            SIZES[size],
            contentClassName,
          )}
          style={{ width: "calc(100vw - 32px)" }}
        >
          {title && (
            <Dialog.Title className="px-6 pt-6 pb-2 font-title text-2xl text-navy">
              {title}
            </Dialog.Title>
          )}
          {description && (
            <Dialog.Description className="px-6 pb-2 text-sm text-gray-600">
              {description}
            </Dialog.Description>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const ModalTitle = Dialog.Title;
export const ModalDescription = Dialog.Description;

export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <footer className={cn("flex justify-end gap-3 border-t border-mist px-6 py-4", className)}>
      {children}
    </footer>
  );
}
